import {
  Button,
  CheckboxField,
  Flex,
  ResponsiveModalContent,
  ResponsiveModalFooter,
  ResponsiveModalHeader,
  useModalControlContext,
} from '@bridebook/aura';
import { TextArea } from '@bridebook/ui';
import { interactedWithGuestCommsAction } from '@bridebook/web/lib/inbox/guests/analytics/actions';
import { useCreateMessageFlow } from '@bridebook/web/lib/inbox/guests/create-message-flow/create-message-flow-context';
import { useDirtyMessageFlow } from '@bridebook/web/lib/inbox/guests/create-message-flow/dirty-message-flow-context';
import { useDispatch } from '@bridebook/web/lib/utils';
import { zodResolver } from '@hookform/resolvers/zod';
import { TFunction, useTranslation } from 'next-i18next';
import { useEffect } from 'react';
import { Controller, FormProvider, useForm } from 'react-hook-form';
import { z } from 'zod';
import { CharacterCounter, Label } from '../components/form-components';
import styles from './message-form.style';
import { WeddingWebsiteSection } from './website-link-section';

const MAX_SUBJECT_LENGTH = 50;
const MAX_MESSAGE_LENGTH = 2500;

const getFormSchema = (t: TFunction) =>
  z.object({
    subject: z
      .string()
      .min(1, {
        message: t('inbox:guests.createMessageFlow.messageForm.subject.required'),
      })
      .max(MAX_SUBJECT_LENGTH, {
        message: t('inbox:guests.createMessageFlow.messageForm.subject.maxLength', {
          maxLength: MAX_SUBJECT_LENGTH,
        }),
      }),
    message: z
      .string()
      .min(1, {
        message: t('inbox:guests.createMessageFlow.messageForm.message.required'),
      })
      .max(MAX_MESSAGE_LENGTH, {
        message: t('inbox:guests.createMessageFlow.messageForm.message.maxLength', {
          maxLength: MAX_MESSAGE_LENGTH,
        }),
      }),
    includeGuestsNames: z.boolean(),
    shareWeddingWebsiteLink: z.boolean(),
    displayPasswordInEmail: z.boolean(),
  });

export type MessageFormSchema = z.infer<ReturnType<typeof getFormSchema>>;

export const MessageForm = () => {
  const { t } = useTranslation('inbox');
  const dispatch = useDispatch();
  const { goToNextStep, updateFormData, formData } = useCreateMessageFlow();
  const { setIsDirty } = useDirtyMessageFlow();
  const { close: closeModal } = useModalControlContext();

  const formMethods = useForm({
    defaultValues: formData.message,
    resolver: zodResolver(getFormSchema(t)),
    mode: 'onChange',
  });

  const {
    handleSubmit,
    control,
    trigger,
    formState: { isValid, isDirty, isSubmitting },
  } = formMethods;

  // sync form's isDirty with the dirty context (displays intercept when closing modal)
  useEffect(() => {
    setIsDirty(isDirty);
  }, [isDirty, setIsDirty]);

  const isSubmitButtonDisabled =
    isSubmitting || (!isDirty && !formData.message?.subject) || !isValid;

  const onSubmit = (data: MessageFormSchema) => {
    updateFormData({
      message: {
        subject: data.subject,
        message: data.message,
        includeGuestsNames: data.includeGuestsNames,
        shareWeddingWebsiteLink: data.shareWeddingWebsiteLink,
        displayPasswordInEmail: data.displayPasswordInEmail,
      },
    });
    goToNextStep();
    dispatch(
      interactedWithGuestCommsAction({
        action: 'clicked',
        location: 'guestComms',
        section: 'writeMessage',
        element: 'continueButton',
        elementLabel: 'Continue to add guests',
        websiteShareLink: data.shareWeddingWebsiteLink,
        includeGuestName: data.includeGuestsNames,
        displayPassword: data.displayPasswordInEmail,
        subjectLine: data.subject,
      }),
    );
  };

  // This is to trigger the validation when going back from the next step. So the data is already filled via default values, but it needs to be validated again.
  useEffect(() => {
    // If we have prefilled data, trigger validation and mark form as dirty
    if (formData.message?.subject || formData.message?.message) {
      trigger();
    }
  }, [formData.message?.message, formData.message?.subject, trigger]);

  return (
    <>
      <ResponsiveModalHeader onBackButtonClick={closeModal}>
        {t('guests.createMessageFlow.messageForm.header')}
      </ResponsiveModalHeader>
      <ResponsiveModalContent className="xl:pt-4">
        <FormProvider {...formMethods}>
          <form
            onSubmit={handleSubmit(onSubmit)}
            id="message-form"
            className="w-full xl:px-[100px] xl:pt-0">
            {/* SUBJECT */}
            <Flex className="flex-col gap-2">
              <Label htmlFor="subject">
                {t('guests.createMessageFlow.messageForm.subject.label')}
              </Label>
              <Controller
                control={control}
                name="subject"
                render={({ field: { onChange, value, onBlur }, fieldState: { error } }) => (
                  <Flex className="flex-col gap-1">
                    <TextArea
                      name="subject"
                      id="subject"
                      autoFocus={false}
                      controlled
                      label={t('guests.createMessageFlow.messageForm.subject.innerLabel')}
                      onChange={onChange}
                      value={value}
                      onBlur={onBlur}
                      error={error?.message}
                      placeholder={t('guests.createMessageFlow.messageForm.subject.placeholder')}
                      withAdjustableHeight={value.length > 0}
                      rows={1}
                      innerWrapperStyle={styles.textAreaInnerWrapper}
                    />
                    <CharacterCounter error={!!error?.message}>
                      {value?.length}/{MAX_SUBJECT_LENGTH}
                    </CharacterCounter>
                  </Flex>
                )}
              />
            </Flex>
            {/* MESSAGE */}
            <Flex className="flex-col gap-2 py-2">
              <Label htmlFor="message">
                {t('guests.createMessageFlow.messageForm.message.label')}
              </Label>
              <div className="flex h-[54px] items-center">
                <Controller
                  control={control}
                  name="includeGuestsNames"
                  render={({ field: { value, onChange } }) => (
                    <CheckboxField
                      id="includeGuestsNames"
                      label={t('guests.createMessageFlow.messageForm.includeGuestName.label')}
                      checked={value}
                      onCheckedChange={onChange}
                    />
                  )}
                />
              </div>
              <Controller
                control={control}
                name="message"
                render={({ field: { onChange, value, onBlur }, fieldState: { error } }) => (
                  <Flex className="flex-col gap-1">
                    <TextArea
                      name="message"
                      id="message"
                      autoFocus={false}
                      controlled
                      label={t('guests.createMessageFlow.messageForm.message.innerLabel')}
                      onChange={onChange}
                      value={value}
                      onBlur={onBlur}
                      error={error?.message}
                      placeholder={t('guests.createMessageFlow.messageForm.message.placeholder')}
                      withAdjustableHeight
                      rows={5}
                      innerWrapperStyle={styles.textAreaInnerWrapper}
                    />
                    <CharacterCounter error={!!error?.message}>
                      {value?.length}/{MAX_MESSAGE_LENGTH}
                    </CharacterCounter>
                  </Flex>
                )}
              />
            </Flex>
            <WeddingWebsiteSection />
          </form>
        </FormProvider>
      </ResponsiveModalContent>
      <ResponsiveModalFooter className="border-t border-space-15">
        <Button
          variant="primary"
          className="w-full max-w-[408px]"
          type="submit"
          form="message-form"
          disabled={isSubmitButtonDisabled}>
          {t('guests.createMessageFlow.messageForm.submitButton')}
        </Button>
      </ResponsiveModalFooter>
    </>
  );
};
